"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateProjectSettings = exports.readProjectSettings = exports.defaultProjectSettingsFor = exports.DEFAULT_MOBILE_PROJECT_SETTINGS = exports.DEFAULT_WEB_PROJECT_SETTINGS = exports.DEFAULT_URL_MODE = exports.URL_MODES = void 0;
const http_assert_1 = __importDefault(require("http-assert"));
const projectTypes_1 = require("./projectTypes");
/**
 * A web project's Unique URLs option: which of two named options decides
 * whether two addresses are the same page.
 *
 * - `full_url` keeps the query string in the page's identity, so
 *   `/search?q=a` and `/search?q=b` are two different pages.
 * - `url_without_query` drops the whole query string before comparing, so those
 *   two are the same page. The path is kept, so `/search` and `/checkout` stay
 *   distinct.
 *
 * A named option rather than a boolean because the value is published on the
 * project read response, so it is a public contract from the day it exists. A
 * third option can be added later without changing that field's name, type or
 * meaning — which the Type 3 fuzzy-clustering work is expected to want.
 */
exports.URL_MODES = ['full_url', 'url_without_query'];
/** Full URL — today's behavior, and what every web project is backfilled with. */
exports.DEFAULT_URL_MODE = 'full_url';
/** What a web project gets when the caller expresses no preference. */
exports.DEFAULT_WEB_PROJECT_SETTINGS = Object.freeze({ url_mode: exports.DEFAULT_URL_MODE });
/** What a mobile project gets: it has no URL to compare, so it has no mode. */
exports.DEFAULT_MOBILE_PROJECT_SETTINGS = Object.freeze({ url_mode: null });
/**
 * The single definition of "the default for a type", so the create paths
 * cannot drift apart. The one-off migration names the slugs itself, because it
 * must keep describing the world as it was on the day it ran.
 */
const defaultProjectSettingsFor = (selected_project_type) => (0, projectTypes_1.isMobileProjectType)(selected_project_type)
    ? { ...exports.DEFAULT_MOBILE_PROJECT_SETTINGS }
    : { ...exports.DEFAULT_WEB_PROJECT_SETTINGS };
exports.defaultProjectSettingsFor = defaultProjectSettingsFor;
const isUrlMode = (value) => exports.URL_MODES.includes(value);
const isPlainObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/** The allowed options as a message lists them, so every rejection reads alike. */
const ALLOWED_URL_MODES = exports.URL_MODES.map(mode => `\`${mode}\``).join(' or ');
/**
 * The read boundary: the stored column as every read response reports it.
 *
 * The project type decides the shape, not the stored bytes. A mobile project
 * always reports `url_mode: null`, whatever a pre-migration row or a stale pod
 * left in the column, and a web project always reports an option — anything
 * unrecognized reads as Full URL rather than being passed through. Lossy on
 * purpose, so no reader has to test for a missing or foreign value.
 */
const readProjectSettings = (stored, selected_project_type) => {
    if ((0, projectTypes_1.isMobileProjectType)(selected_project_type)) {
        return { url_mode: null };
    }
    const url_mode = isPlainObject(stored) ? stored.url_mode : null;
    return { url_mode: isUrlMode(url_mode) ? url_mode : exports.DEFAULT_URL_MODE };
};
exports.readProjectSettings = readProjectSettings;
/**
 * The single enforcement point for a settings value, shared by the DevHub
 * project endpoints and the jazzband routes behind them. Callers resolve
 * "the caller sent nothing" themselves -- a create to the project type's
 * default, an update to the stored value -- so this always sees an object.
 *
 * `url_mode`'s legal value depends on the project type, and each type's guard
 * names its own rule: `null` for a mobile project, which has no URL to
 * compare, and one of the two options for a web project. Sending the wrong one
 * for the type is a 400 rather than a silently ignored field.
 *
 * Nothing is trimmed or lower-cased on the way in -- a value differing from an
 * option only by case or whitespace is a client mistake worth naming, not a
 * value to guess at.
 *
 * @returns the normalized settings.
 * @throws a 400 naming the problem, never a generic validation error.
 */
const validateProjectSettings = ({ selected_project_type, project_settings }) => {
    (0, http_assert_1.default)(isPlainObject(project_settings), 400, 'Project settings must be an object with a `url_mode`.');
    const url_mode = project_settings.url_mode;
    if ((0, projectTypes_1.isMobileProjectType)(selected_project_type)) {
        (0, http_assert_1.default)(url_mode === null, 400, '`url_mode` is not applicable to mobile projects — send `url_mode: null`.');
        return { url_mode: null };
    }
    (0, http_assert_1.default)(isUrlMode(url_mode), 400, url_mode === null || url_mode === undefined
        ? 'Project settings: `url_mode` is required for a web project — ' +
            `one of ${ALLOWED_URL_MODES}.`
        : `URL mode ${JSON.stringify(url_mode)} is not a Unique URLs option — ` +
            `\`url_mode\` must be one of ${ALLOWED_URL_MODES}.`);
    return { url_mode };
};
exports.validateProjectSettings = validateProjectSettings;
//# sourceMappingURL=projectSettings.js.map